import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PageHeader } from '../components/ui/responsive-table';
import {
  useMassageCategories,
  useCreateMassageCategory,
  useUpdateMassageCategory,
  useDeleteMassageCategory,
  useSoinCategories,
  useCreateSoinCategory,
  useUpdateSoinCategory,
  useDeleteSoinCategory,
} from '../hooks/useServiceCategories';
import { Plus, Pencil, Trash2, Loader2, GripVertical } from 'lucide-react';
import { generateSlug } from '../lib/utils';
import type { ServiceCategory } from '../types/entities';

const categorySchema = z.object({
  name: z.string().min(1, 'Nom requis'),
  slug: z.string().min(1, 'Slug requis'),
  description: z.string().nullable().optional(),
  order: z.number().int().min(0).optional(),
});

type CategoryFormData = z.infer<typeof categorySchema>;

interface CategoryListProps {
  categories: ServiceCategory[] | undefined;
  isLoading: boolean;
  onEdit: (category: ServiceCategory) => void;
  onDelete: (id: string) => void;
  deleteConfirm: string | null;
  setDeleteConfirm: (id: string | null) => void;
  type: 'massage' | 'soin';
}

function CategoryList({
  categories,
  isLoading,
  onEdit,
  onDelete,
  deleteConfirm,
  setDeleteConfirm,
  type,
}: CategoryListProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Aucune catégorie de {type === 'massage' ? 'massage' : 'soin'} pour le moment
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {categories.map((category) => (
        <div
          key={category.id}
          className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4"
        >
          <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0 cursor-move" />

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">{category.name}</h3>
            <p className="text-sm text-gray-500">{category.slug}</p>
            {category.description && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-1">{category.description}</p>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="bg-gray-100 px-2 py-1 rounded">
              {category._count?.[type === 'massage' ? 'massages' : 'soins'] || 0} {type === 'massage' ? 'massage(s)' : 'soin(s)'}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onEdit(category)}>
              <Pencil className="w-4 h-4" />
            </Button>
            {deleteConfirm === category.id ? (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDelete(category.id)}
                >
                  Confirmer
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Annuler
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteConfirm(category.id)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ServiceCategories() {
  const [activeTab, setActiveTab] = useState<'massage' | 'soin'>('massage');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Massage categories hooks
  const { data: massageCategories, isLoading: massageLoading } = useMassageCategories();
  const createMassageCategory = useCreateMassageCategory();
  const updateMassageCategory = useUpdateMassageCategory();
  const deleteMassageCategory = useDeleteMassageCategory();

  // Soin categories hooks
  const { data: soinCategories, isLoading: soinLoading } = useSoinCategories();
  const createSoinCategory = useCreateSoinCategory();
  const updateSoinCategory = useUpdateSoinCategory();
  const deleteSoinCategory = useDeleteSoinCategory();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: '',
      slug: '',
      description: null,
      order: 0,
    },
  });

  const handleOpenCreate = () => {
    reset({
      name: '',
      slug: '',
      description: null,
      order: activeTab === 'massage'
        ? (massageCategories?.length || 0)
        : (soinCategories?.length || 0),
    });
    setEditingCategory(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (category: ServiceCategory) => {
    reset({
      name: category.name,
      slug: category.slug,
      description: category.description,
      order: category.order,
    });
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (activeTab === 'massage') {
      await deleteMassageCategory.mutateAsync(id);
    } else {
      await deleteSoinCategory.mutateAsync(id);
    }
    setDeleteConfirm(null);
  };

  const onSubmit = async (data: CategoryFormData) => {
    if (activeTab === 'massage') {
      if (editingCategory) {
        await updateMassageCategory.mutateAsync({ id: editingCategory.id, data });
      } else {
        await createMassageCategory.mutateAsync(data);
      }
    } else {
      if (editingCategory) {
        await updateSoinCategory.mutateAsync({ id: editingCategory.id, data });
      } else {
        await createSoinCategory.mutateAsync(data);
      }
    }
    setIsModalOpen(false);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setValue('name', newName);
    if (!editingCategory) {
      setValue('slug', generateSlug(newName));
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <PageHeader
          title="Catégories de services"
          description="Gérez les catégories de massages et de soins"
          action={
            <Button onClick={handleOpenCreate} className="gap-2">
              <Plus className="w-4 h-4" />
              Nouvelle catégorie
            </Button>
          }
        />

        <Tabs value={activeTab} onValueChange={(v: string) => setActiveTab(v as 'massage' | 'soin')}>
          <TabsList className="mb-6">
            <TabsTrigger value="massage">Catégories de massages</TabsTrigger>
            <TabsTrigger value="soin">Catégories de soins</TabsTrigger>
          </TabsList>

          <TabsContent value="massage">
            <CategoryList
              categories={massageCategories}
              isLoading={massageLoading}
              onEdit={handleOpenEdit}
              onDelete={handleDelete}
              deleteConfirm={deleteConfirm}
              setDeleteConfirm={setDeleteConfirm}
              type="massage"
            />
          </TabsContent>

          <TabsContent value="soin">
            <CategoryList
              categories={soinCategories}
              isLoading={soinLoading}
              onEdit={handleOpenEdit}
              onDelete={handleDelete}
              deleteConfirm={deleteConfirm}
              setDeleteConfirm={setDeleteConfirm}
              type="soin"
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal Form */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">
                {editingCategory
                  ? 'Modifier la catégorie'
                  : `Nouvelle catégorie de ${activeTab === 'massage' ? 'massage' : 'soin'}`}
              </h2>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom *</Label>
                <Input
                  id="name"
                  {...register('name')}
                  onChange={handleNameChange}
                  className={errors.name ? 'border-red-500' : ''}
                  placeholder="Ex: Massages relaxants"
                />
                {errors.name && (
                  <p className="text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Slug *</Label>
                <Input
                  id="slug"
                  {...register('slug')}
                  className={errors.slug ? 'border-red-500' : ''}
                  placeholder="Ex: massages-relaxants"
                />
                {errors.slug && (
                  <p className="text-sm text-red-600">{errors.slug.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  rows={3}
                  placeholder="Description optionnelle de la catégorie"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="order">Ordre d'affichage</Label>
                <Input
                  id="order"
                  type="number"
                  {...register('order', { valueAsNumber: true })}
                  min="0"
                />
                <p className="text-xs text-gray-500">
                  Les catégories sont affichées par ordre croissant
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                >
                  Annuler
                </Button>
                <Button type="submit">
                  {editingCategory ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
